<template>
  <div id="kanban-col-headers" v-fix-to-top="{ placeholder: 'table-header-placeholder', topbar: '#topbar', xScroll: '#table-wrapper' }" class="display-flex">
    <draggable v-if="canModifyPlanning" :list="processSteps" :options="{ handle: '.horizontal-draggable-anchor' }" class="display-flex"
               @start="startSortSteps()" @end="updateSortSteps()">
      <div v-for="col in processSteps" :id="`kanbanheader-${col.id}`" :key="col.id"
           class="kanban-col-header designcolor-main-bg hovervisible-activator mx-2"
           :style="{ 'background-color': col.getMainColor(), width: (col.width - 16) + 'px' }">
        <div v-if="col.width >= defaultColWidth" :title="$t('KANBAN.DRAG_AND_DROP_STEPS')" class="horizontal-draggable-anchor grab hovervisible-item"></div>

        <div v-if="col.width >= defaultColWidth" class="d-flex align-stretch">
          <!-- Icon -->
          <div v-if="icon = col.hasIcon() && col.getIcon()" class="flex-center-center pointer" style="background: #ffffffaa; flex: 0 0 36px" @click.stop="selectColHeaderColor(col)">
            <v-icon :color="icon.color" :title="icon.label || colIconDefaultLabel(col)" size="16">
              {{ icon.name }}
            </v-icon>
          </div>
          <!-- Label -->
          <input :ref="`colHeaderInput${col.id}`" v-model.trim="col.label" v-contentautoselect :disabled="! col.id" spellcheck="false"
                 class="text-ellipsis text-center px-1 my-1" style="width: 100%"
                 @focus="startChangingStep" @blur="changingStep">
          <!-- Menu -->
          <div>
            <v-menu offset-y left style="z-index: 999">
              <template #activator="{ on }">
                <div v-show="col.width >= defaultColWidth" :title="$t('KANBAN.CONFIGURE_COLUMN')" class="col-context-menu flex-center-center pointer" v-on="on">
                  <span class="fas fa-ellipsis-v"></span>
                </div>
              </template>
              <v-list dense>
                <v-list-item class="primary--text" @click="toggleColDisplay(col)">
                  <v-icon small class="mr-1" color="accent" style="width: 24px; color: inherit">fas fa-arrow-from-right</v-icon>{{ $t('KANBAN.HIDE_COLUMN') }}
                </v-list-item>
                <v-list-item v-if="col.id" class="primary--text" @click="editColTitle(col)">
                  <v-icon small class="mr-1" style="width: 24px; color: inherit">fas fa-pen</v-icon>{{ $t('GLOBAL.RENAME') }}
                </v-list-item>
                <v-list-item class="primary--text" @click="selectColHeaderColor(col)">
                  <v-icon small class="mr-1" color="accent" style="width: 24px; color: inherit">fas fa-tint</v-icon>{{ $t('PLANNING.SELECT_ICON_AND_COLOR') }}
                </v-list-item>
                <v-list-item class="primary--text" @click="toggleColSize(col)">
                  <v-icon small class="mr-1" style="width: 24px; color: inherit">fas fa-arrows-alt-h</v-icon>
                  {{ col.width <= defaultColWidth ? $t('KANBAN.EXPAND_COLUMN') : $t('KANBAN.REDUCE_COLUMN') }}
                </v-list-item>
                <v-list-item v-if="col.id" class="errorred--text" @click="deleteStep(col.id)">
                  <v-icon small class="mr-1" style="width: 24px; color: inherit">fas fa-trash-alt</v-icon>{{ $t('GLOBAL.DELETE') }}
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

        <!-- Hidden col -->
        <div v-else :title="`${$t('KANBAN.SHOW_COLUMN')} ${col.label}`" class="col-context-menu flex-center-center pointer hovervisible-item" style="height: 36px"
             @click="toggleColDisplay(col)">
          <span class="fas fa-arrow-from-left"></span>
        </div>
      </div>
    </draggable>
    <div v-else class="display-flex">
      <div v-for="col in processSteps" :id="`kanbanheader-${col.id}`" :key="col.id"
           class="kanban-col-header designcolor-main-bg mx-2"
           :style="{ 'background-color': col.getMainColor(), width: (col.width - 16) + 'px' }">
        <div :title="col.label" class="d-flex align-stretch">
          <!-- Icon -->
          <div v-if="icon = col.hasIcon() && col.getIcon()" class="flex-center-center" style="background: #ffffffaa; flex: 0 0 36px">
            <v-icon :color="icon.color" :title="icon.label || colIconDefaultLabel(col)" size="16">
              {{ icon.name }}
            </v-icon>
          </div>
          <!-- Label -->
          <div class="text-ellipsis text-center px-1 my-1 nobr" style="width: 100%">{{ col.label }}</div>
        </div>
      </div>
    </div>

    <!-- Subprojects cols -->
    <div class="display-flex">
      <div v-for="col in subprojectsProcessSteps" :id="`kanbanheader-${col.id}`" :key="col.id"
           class="kanban-col-header designcolor-main-bg mx-2"
           :style="{ 'background-color': col.getMainColor(), width: (col.width - 16) + 'px' }">
        <div :title="col.label" class="d-flex align-stretch">
          <!-- Icon -->
          <div v-if="icon = col.hasIcon() && col.getIcon()" class="flex-center-center" style="background: #ffffffaa; flex: 0 0 36px">
            <v-icon :color="icon.color" :title="icon.label || colIconDefaultLabel(col)" size="16">
              {{ icon.name }}
            </v-icon>
          </div>
          <!-- Label -->
          <div class="kanban-subproject-col-label text-ellipsis text-center px-1 my-1 nobr" style="width: 100%">
            {{ col.label }}
            <div class="small">({{ $t('PLANNING.SUBPROJECT').toLowerCase() }})</div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALS -->
    <modal v-if="openedColorPickerColHeader" :backdrop="false" :target="`#kanbanheader-${openedColorPickerColHeader.id}`" movable size="md" @close="closeColorEditingModal">
      <color-picker :planning="planning" :target="openedColorPickerColHeader" @close="closeColorEditingModal"></color-picker>
    </modal>
  </div>
</template>

<style lang="scss">
  #kanban-col-headers {
    background: white;
    padding-top: 5px;
  }

  #kanban-col-headers.fixed {
    position: fixed;
    top: 0px;
    z-index: 300;
    width: inherit;
    box-shadow: 0px 1px 10px grey;
    transform: translateZ(0);
  }

  .kanban-col-header {
    font-weight: bold;
    line-height: 28px;
    position: relative;
    border-radius: 5px;

    .horizontal-draggable-anchor {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      border-radius: 5px 5px 0 0;
    }

    .kanban-subproject-col-label {
      line-height: 14px;
    }
  }
</style>

<script>
  import { mapActions } from 'vuex';
  import draggable from 'vuedraggable';
  import constants from '@/js/constants.js';
  import PlanningProcessStep from '@/models/PlanningProcessStep';
  import ColorPicker from '@/components/ColorPicker/ColorPicker';

  export default {
    components: {
      draggable,
      ColorPicker,
    },
    props: {
      planning: { type: Object, required: true },
      dynamic: { type: Boolean, default: false },
      view: { type: Boolean, default: false },
    },
    data() {
      const defaultColWidth = constants.kanbanColWidth;

      return {
        defaultColWidth,
        defaultProcessSteps: this.$store.getters['users/accessRight/config'].processSteps.map((step) => {
          const width = step.id ? defaultColWidth : 1.5 * defaultColWidth;
          return new PlanningProcessStep(this.planning, { ...step, width });
        }),
        openedColorPickerColHeader: null,
      };
    },
    computed: {
      processSteps: {
        get() {
          return this.planning.process.steps;
        },
        set(newVal) {
          this.planning.process.steps = newVal;
        },
      },
      subprojectsProcessSteps() {
        return this.$store.getters['planning/process/subprojectsProcessSteps'];
      },
      canModifyPlanning() {
        return this.dynamic && ! this.view;
      },
    },
    created() {
      if (! this.processSteps.length) this.processSteps = [this.defaultProcessSteps[0]]; // Backlog
    },
    methods: {
      addCol() {
        if (this.processSteps.length == 1) { // only Backlog
          this.addDefaultSteps(this.defaultProcessSteps.filter(step => step.id));
        } else {
          this.addStep(null);
        }
      },
      editColTitle(col) {
        this.$refs[`colHeaderInput${col.id}`][0].focus();
      },
      selectColHeaderColor(col) {
        this.startChangingStep();
        this.openedColorPickerColHeader = col;
      },
      closeColorEditingModal() {
        this.changingStep();
        this.openedColorPickerColHeader = null;
      },
      toggleColDisplay(col) {
        this.startChangingStep();
        col.width = col.width < this.defaultColWidth ? this.defaultColWidth : 30;
        this.changingStep();
      },
      toggleColSize(col) {
        this.startChangingStep();
        col.width = col.width > this.defaultColWidth ? this.defaultColWidth : (1.5 * this.defaultColWidth);
        this.changingStep();
      },
      colIconDefaultLabel(col) {
        return this.$t(`ICONS.${col.getIconId().toUpperCase()}`);
      },
      ...mapActions('planning/process', ['addStep', 'addDefaultSteps', 'deleteStep', 'startSortSteps', 'updateSortSteps', 'startChangingStep', 'changingStep']),
    },
  };
</script>
